import { useState, useCallback, useEffect, useRef } from "react";
import { EditorContent, useEditor, ReactNodeViewRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import { Emoji, emojis } from "@tiptap/extension-emoji";
import { Button } from "../button/button";
import { Separator } from "../separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../tabs";
import { Input, Label, Popover, Select } from "@tellescope/design-system";
import { Progress } from "../progress";
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Undo,
  Redo,
  Link as LinkIcon,
  Image as ImageIcon,
  Upload,
  Loader2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { EmojiPicker } from "../EmojiPicker/EmojiPicker";
import { useTranslation } from "react-i18next";
import { RICH_TEXT_EDITOR_PROSE_CLASSES } from "./constants";
import { ImageNode } from "./ImageNode";

const MAX_IMAGE_SIZE_MB = 5;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];

export type ImageUploadHandler = (file: File) => Promise<{ url: string } | null>;

export interface RichTextEditorProps {
  content?: string;
  onChange?: (content: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  editable?: boolean;
  className?: string;
  onImageUpload?: ImageUploadHandler;
  autoFocus?: boolean;
  allowImages?: boolean;
}

const FONT_SIZES = [8, 10, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96];

const TEXT_ALIGNMENTS = [
  { value: "left", Icon: AlignLeft, label: "Align left" },
  { value: "center", Icon: AlignCenter, label: "Align center" },
  { value: "right", Icon: AlignRight, label: "Align right" },
  { value: "justify", Icon: AlignJustify, label: "Justify" },
] as const;

export const RichTextEditor = ({
  content = "",
  onChange,
  onBlur,
  placeholder = "Start typing...",
  editable = true,
  className,
  onImageUpload,
  autoFocus,
  allowImages = true,
}: RichTextEditorProps) => {
  const { t } = useTranslation("ui");
  const [, setEditorState] = useState(0);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  // Image popover state
  const [imagePopoverOpen, setImagePopoverOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline cursor-pointer",
        },
      }),
      Emoji.configure({
        emojis: emojis,
        enableEmoticons: true,
      }),
      TextStyle,
      FontSize,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      ...(allowImages
        ? [
            Image.extend({
              addAttributes() {
                return {
                  ...this.parent?.(),
                  width: { default: null },
                  height: { default: null },
                };
              },
              addNodeView() {
                return ReactNodeViewRenderer(ImageNode);
              },
            }).configure({
              inline: false,
              allowBase64: true,
            }),
          ]
        : []),
    ],
    content,
    editable,
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
      setEditorState((prev) => prev + 1);
    },
    onTransaction: () => {
      setEditorState((prev) => prev + 1);
    },
    editorProps: {
      attributes: {
        class: cn(...RICH_TEXT_EDITOR_PROSE_CLASSES, "min-h-[200px] p-4 border-0"),
      },
    },
  });

  const openLinkPopover = useCallback(() => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const linkAttrs = editor.getAttributes("link");

    // If we're on an existing link, get the full link text
    if (editor.isActive("link")) {
      // Get the actual range of the link mark
      const { $from } = editor.state.selection;
      const marks = $from.marks();
      const linkMark = marks.find((mark) => mark.type.name === "link");

      if (linkMark) {
        // Find the start and end of the link mark
        let linkStart = from;
        let linkEnd = to;

        // Search backwards for link start
        editor.state.doc.nodesBetween(0, from, (node, pos) => {
          if (
            node.isText &&
            node.marks.some((mark) => mark.type.name === "link" && mark.attrs.href === linkAttrs.href)
          ) {
            linkStart = Math.min(linkStart, pos);
          }
        });

        // Search forwards for link end
        editor.state.doc.nodesBetween(from, editor.state.doc.content.size, (node, pos) => {
          if (
            node.isText &&
            node.marks.some((mark) => mark.type.name === "link" && mark.attrs.href === linkAttrs.href)
          ) {
            linkEnd = Math.max(linkEnd, pos + node.nodeSize);
          }
        });

        const fullLinkText = editor.state.doc.textBetween(linkStart, linkEnd, "");
        setLinkText(fullLinkText);
        setLinkUrl(linkAttrs.href || "");
        setLinkPopoverOpen(true);
        return;
      }
    }

    // If no active link, just use selected text
    const selectedText = editor.state.doc.textBetween(from, to, "");
    setLinkText(selectedText || "");
    setLinkUrl(linkAttrs.href || "");
    setLinkPopoverOpen(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const linkAttrs = editor.getAttributes("link");

    if (linkUrl) {
      // If we're editing an existing link
      if (editor.isActive("link")) {
        // Find the full range of the existing link
        let linkStart = from;
        let linkEnd = to;

        editor.state.doc.nodesBetween(0, from, (node, pos) => {
          if (
            node.isText &&
            node.marks.some((mark) => mark.type.name === "link" && mark.attrs.href === linkAttrs.href)
          ) {
            linkStart = Math.min(linkStart, pos);
          }
        });

        editor.state.doc.nodesBetween(from, editor.state.doc.content.size, (node, pos) => {
          if (
            node.isText &&
            node.marks.some((mark) => mark.type.name === "link" && mark.attrs.href === linkAttrs.href)
          ) {
            linkEnd = Math.max(linkEnd, pos + node.nodeSize);
          }
        });

        // Delete the old link and insert the new one
        editor
          .chain()
          .focus()
          .deleteRange({ from: linkStart, to: linkEnd })
          .insertContentAt(linkStart, {
            type: "text",
            text: linkText,
            marks: [{ type: "link", attrs: { href: linkUrl } }],
          })
          .run();
      } else {
        // Creating a new link
        const selectedText = editor.state.doc.textBetween(from, to, "");

        if (selectedText) {
          // If there's selected text, just add the link mark
          editor.chain().focus().setLink({ href: linkUrl }).run();
        } else if (linkText) {
          // If no selection but we have link text, insert it
          editor
            .chain()
            .focus()
            .insertContent({
              type: "text",
              text: linkText,
              marks: [{ type: "link", attrs: { href: linkUrl } }],
            })
            .run();
        }
      }
    }

    setLinkPopoverOpen(false);
    setLinkText("");
    setLinkUrl("");
  }, [editor, linkText, linkUrl]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
    setLinkPopoverOpen(false);
    setLinkText("");
    setLinkUrl("");
  }, [editor]);

  const insertEmoji = useCallback(
    (emoji: string) => {
      if (!editor) return;
      editor.chain().focus().insertContent(emoji).run();
    },
    [editor]
  );

  const validateImageFile = useCallback(
    (file: File): string | null => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        return t("Invalid image type. Accepted: JPEG, PNG, GIF, WebP, SVG");
      }
      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        return t("Image too large. Maximum size: {{max}}MB", { max: MAX_IMAGE_SIZE_MB });
      }
      return null;
    },
    [t]
  );

  const insertImage = useCallback(
    (src: string, alt: string) => {
      if (!editor) return;
      editor.chain().focus().setImage({ src, alt }).run();
      setImagePopoverOpen(false);
      setImageUrl("");
      setImageAlt("");
      setUploadError(null);
    },
    [editor]
  );

  const handleImageUrlInsert = useCallback(() => {
    if (!imageUrl.trim()) return;
    insertImage(imageUrl.trim(), imageAlt.trim());
  }, [imageUrl, imageAlt, insertImage]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!onImageUpload) return;

      const error = validateImageFile(file);
      if (error) {
        setUploadError(error);
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      try {
        const result = await onImageUpload(file);
        clearInterval(progressInterval);
        setUploadProgress(100);

        if (result?.url) {
          insertImage(result.url, imageAlt.trim() || file.name);
        } else {
          setUploadError(t("Failed to upload image"));
        }
      } catch {
        clearInterval(progressInterval);
        setUploadError(t("Failed to upload image"));
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [onImageUpload, validateImageFile, insertImage, imageAlt, t]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      // Reset input so the same file can be selected again
      e.target.value = "";
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const resetImagePopover = useCallback(() => {
    setImageUrl("");
    setImageAlt("");
    setUploadError(null);
    setIsUploading(false);
    setUploadProgress(0);
  }, []);

  useEffect(() => {
    if (editor?.getHTML() !== content) {
      editor?.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={cn("border rounded-lg overflow-hidden", className)}>
      <div className="border-b p-2 flex flex-wrap items-center gap-1">
        {/* Text Formatting Toggles */}
        <Button
          variant={editor.isActive("bold") ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          aria-label="Toggle bold"
          type="button"
        >
          <Bold className="h-4 w-4" />
        </Button>

        <Button
          variant={editor.isActive("italic") ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          aria-label="Toggle italic"
          type="button"
        >
          <Italic className="h-4 w-4" />
        </Button>

        <Button
          variant={editor.isActive("strike") ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          aria-label="Toggle strikethrough"
          type="button"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>

        <Button
          variant={editor.isActive("code") ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          aria-label="Toggle code"
          type="button"
        >
          <Code className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Select
          value={editor.getAttributes("textStyle").fontSize ?? ""}
          onValueChange={(fontSize) =>
            fontSize ? editor.chain().focus().setFontSize(fontSize).run() : editor.chain().focus().unsetFontSize().run()
          }
        >
          <Select.Trigger className="h-8 w-[72px]" aria-label={t("Font size")}>
            <Select.Value placeholder={t("Size")} />
          </Select.Trigger>
          <Select.Content>
            {FONT_SIZES.map((size) => (
              <Select.Item key={size} value={`${size}px`}>
                {size}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>

        <Separator orientation="vertical" className="h-6" />

        {/* Text Alignment */}
        {TEXT_ALIGNMENTS.map(({ value, Icon, label }) => (
          <Button
            key={value}
            variant={editor.isActive({ textAlign: value }) ? "default" : "ghost"}
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign(value).run()}
            aria-label={label}
            type="button"
          >
            <Icon className="h-4 w-4" />
          </Button>
        ))}

        <Separator orientation="vertical" className="h-6" />

        {/* Link Button with Popover */}
        <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
          <Popover.Trigger asChild>
            <Button
              variant={editor.isActive("link") ? "default" : "ghost"}
              size="sm"
              onClick={openLinkPopover}
              aria-label="Set link"
              type="button"
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </Popover.Trigger>
          <Popover.Content className="w-80">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">{t("Insert Link")}</h4>
                <p className="text-sm text-muted-foreground">{t("Add a hyperlink to your text")}</p>
              </div>
              <div className="grid gap-2">
                <div className="grid gap-1">
                  <Label htmlFor="link-text">{t("Display Text")}</Label>
                  <Input
                    id="link-text"
                    placeholder="Link text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="link-url">URL</Label>
                  <Input
                    autoFocus={!!linkText}
                    id="link-url"
                    placeholder="https://example.com"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        applyLink();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                {editor.isActive("link") && (
                  <Button onClick={removeLink} size="sm" variant="destructive" className="flex-1">
                    {t("Remove Link")}
                  </Button>
                )}
                <Button onClick={applyLink} size="sm" className="flex-1">
                  {t("Apply")}
                </Button>
              </div>
            </div>
          </Popover.Content>
        </Popover>

        {/* Emoji Picker */}
        <EmojiPicker onEmojiSelect={insertEmoji} />

        {/* Image Button with Popover */}
        {allowImages && onImageUpload && (
          <Popover
            open={imagePopoverOpen}
            onOpenChange={(open) => {
              setImagePopoverOpen(open);
              if (!open) resetImagePopover();
            }}
          >
            <Popover.Trigger asChild>
              <Button variant="ghost" size="sm" aria-label={t("Insert Image")} type="button">
                <ImageIcon className="h-4 w-4" />
              </Button>
            </Popover.Trigger>
            <Popover.Content className="w-96">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">{t("Insert Image")}</h4>
                  <p className="text-sm text-muted-foreground">{t("Upload an image or enter a URL")}</p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="image-alt">{t("Alt Text")}</Label>
                  <Input
                    id="image-alt"
                    placeholder={t("Describe the image for accessibility")}
                    value={imageAlt}
                    onChange={(e) => setImageAlt(e.target.value)}
                  />
                </div>

                <Tabs defaultValue="upload" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="upload">{t("Upload")}</TabsTrigger>
                    <TabsTrigger value="url">{t("URL")}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="upload" className="mt-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES.join(",")}
                      onChange={handleFileInputChange}
                      className="hidden"
                    />

                    <div
                      className={cn(
                        "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                        isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25",
                        isUploading && "pointer-events-none opacity-50"
                      )}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      {isUploading ? (
                        <div className="space-y-2">
                          <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">{t("Uploading...")}</p>
                          <Progress value={uploadProgress} className="h-2" />
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">{t("Drop image here or click to browse")}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("Maximum size: {{max}}MB", { max: MAX_IMAGE_SIZE_MB })}
                          </p>
                        </>
                      )}
                    </div>

                    {uploadError && <p className="text-sm text-destructive mt-2">{uploadError}</p>}
                  </TabsContent>

                  <TabsContent value="url" className="mt-4">
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="image-url">{t("Image URL")}</Label>
                        <Input
                          id="image-url"
                          placeholder="https://example.com/image.jpg"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleImageUrlInsert();
                            }
                          }}
                        />
                      </div>
                      <Button onClick={handleImageUrlInsert} size="sm" disabled={!imageUrl.trim()}>
                        {t("Insert")}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </Popover.Content>
          </Popover>
        )}

        <Separator orientation="vertical" className="h-6" />

        {/* Heading Toggles */}
        <Button
          variant={editor.isActive("heading", { level: 1 }) ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          aria-label="Toggle heading 1"
          type="button"
        >
          <Heading1 className="h-4 w-4" />
        </Button>

        <Button
          variant={editor.isActive("heading", { level: 2 }) ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          aria-label="Toggle heading 2"
          type="button"
        >
          <Heading2 className="h-4 w-4" />
        </Button>

        <Button
          variant={editor.isActive("heading", { level: 3 }) ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          aria-label="Toggle heading 3"
          type="button"
        >
          <Heading3 className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        {/* List Toggles */}
        <Button
          variant={editor.isActive("bulletList") ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          aria-label="Toggle bullet list"
          type="button"
        >
          <List className="h-4 w-4" />
        </Button>

        <Button
          variant={editor.isActive("orderedList") ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          aria-label="Toggle ordered list"
          type="button"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <Button
          variant={editor.isActive("blockquote") ? "default" : "ghost"}
          size="sm"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          aria-label="Toggle blockquote"
          type="button"
        >
          <Quote className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        {/* Utility Buttons */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          aria-label="Insert horizontal rule"
          type="button"
        >
          <Minus className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        {/* History Controls */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          aria-label="Undo"
          type="button"
        >
          <Undo className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          aria-label="Redo"
          type="button"
        >
          <Redo className="h-4 w-4" />
        </Button>
      </div>

      <EditorContent editor={editor} placeholder={placeholder} onBlur={onBlur} />
    </div>
  );
};
